import React from "react";
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

/**
 * Reconciliation gate — appears when a cancellation targets an
 * appointment whose linked invoice still has money on it. Two flavours:
 *   • `dialog: "deposit"` — OPEN invoice with a PARTIAL payment (real
 *     deposit). Title: "Deposit Received detected". Actions: Refund
 *     to source (which the backend logs as a Void on the same
 *     business day, or as a Refund on stale invoices) / Move to
 *     Points 100 %.
 *   • `dialog: "refund"` — CLOSED / completed invoice fully paid at
 *     an earlier step (Final Payment). Title: "Refund required".
 *     Same actions, but the primary button says "Refund" and the
 *     amber dot flips to rose so the operator immediately knows
 *     this is a stale-ledger refund, not a same-day void.
 * `depositDialog` shape:
 *   { orders: [...], total_deposit, can_convert, dialog, title,
 *     message, pending: {...} } | null.
 */
export default function DepositActionDialog({ depositDialog, onApply, onClose }) {
  const isRefund = depositDialog?.dialog === "refund";
  const dotClass = isRefund ? "bg-rose-500" : "bg-amber-500";
  const title = depositDialog?.title || (isRefund ? "Refund required" : "Deposit Received detected");
  const noun = isRefund ? "final payment" : "deposit";
  const primaryVerb = isRefund ? "Refund to source" : "Void to source";
  return (
    <AlertDialog open={!!depositDialog} onOpenChange={(o) => !o && onClose()}>
      <AlertDialogContent data-testid="deposit-action-dialog" className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <span className={`inline-block h-2.5 w-2.5 rounded-full ${dotClass}`} />
            <span data-testid="deposit-dialog-title">{title}</span>
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>How would you like to handle this {noun}?</p>
              <div className={`rounded-md border p-3 text-sm space-y-1 ${isRefund ? "bg-rose-50/50" : "bg-amber-50/50"}`} data-testid="deposit-summary">
                <div className="font-medium">
                  {(depositDialog?.orders || []).length} invoice(s) · Rp {Math.round(depositDialog?.total_deposit || 0).toLocaleString("en-US")}
                </div>
                <ul className="text-xs text-muted-foreground space-y-0.5 pl-4 list-disc">
                  {(depositDialog?.orders || []).map((o) => (
                    <li key={o.order_id}>
                      <span className="font-mono">#{o.order_number}</span> — Rp {Math.round(o.amount_paid).toLocaleString("en-US")} paid
                      {o.payment_status === "partially_paid" && <span className="ml-1 text-amber-700">(partially paid)</span>}
                      {o.payment_status === "paid" && <span className="ml-1 text-rose-700">(fully paid · closed)</span>}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-xs text-muted-foreground">
                <b>{primaryVerb}</b> reverses every payment back to its original source (cash / card / voucher / points).
                {!isRefund && <> On the same business day this is logged as a <b>Void</b>; on a stale invoice it's logged as a Refund.</>}
                <br /><b>Move to Points 100%</b> converts the {noun} to loyalty points on the client&apos;s profile — logged as a Deposit Conversion, not new revenue.
                {depositDialog?.can_convert === false && (
                  <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-amber-800">
                    One or more invoices are walk-ins with no customer on file — Move to Points is unavailable. Please refund the deposit, or add a customer to the invoice first.
                  </div>
                )}
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <AlertDialogCancel data-testid="deposit-action-cancel">Never mind</AlertDialogCancel>
          <Button
            variant="outline"
            onClick={() => onApply("refund")}
            data-testid="deposit-action-refund"
            className="border-rose-300 text-rose-700 hover:bg-rose-50"
          >
            {primaryVerb}
          </Button>
          <Button
            onClick={() => onApply("convert_to_points")}
            data-testid="deposit-action-convert"
            disabled={depositDialog?.can_convert === false}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
          >
            Move to Points 100%
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
